import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Download,
  Smartphone,
  Check,
  ArrowLeft,
  Share as ShareIcon,
  Plus,
  MoreVertical,
  Zap,
  WifiOff,
  Bell,
  Sparkles,
} from "lucide-react";
import { motion } from "framer-motion";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/**
 * Detect the visitor's platform so we can show the right walkthrough.
 * Returns one of: "ios", "android", "desktop", "unknown"
 */
function detectPlatform() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (isIOS) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

// -----------------------------------------------------------------------------
// Illustrated step components
// -----------------------------------------------------------------------------

/**
 * A little phone-frame mock so the walkthrough feels visual without needing
 * bitmap screenshots. Draws a rounded phone silhouette with a highlighted
 * "hotspot" indicator where the user should tap.
 */
function PhoneFrame({ children, hotspot }) {
  return (
    <div className="relative mx-auto w-full max-w-[220px]">
      <div className="relative aspect-[9/16] rounded-[2rem] border-[3px] border-foreground/20 bg-background shadow-[0_20px_50px_-15px_rgba(0,0,0,0.15)]">
        {/* Speaker slit */}
        <div className="absolute left-1/2 top-2.5 h-1 w-14 -translate-x-1/2 rounded-full bg-foreground/20" />
        {/* Content area */}
        <div className="absolute inset-2 mt-6 overflow-hidden rounded-[1.5rem] bg-muted/30">
          {children}
        </div>
        {/* Hotspot */}
        {hotspot && (
          <div
            className="absolute z-20"
            style={{
              top: hotspot.top,
              left: hotspot.left,
              right: hotspot.right,
              bottom: hotspot.bottom,
            }}
          >
            <span className="relative flex h-8 w-8">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/50 opacity-75" />
              <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
                <span className="text-xs font-bold">•</span>
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function IosStep1() {
  return (
    <PhoneFrame hotspot={{ bottom: 12, left: "48%" }}>
      {/* Fake Safari browser */}
      <div className="flex h-full flex-col">
        {/* URL bar */}
        <div className="border-b border-border/40 bg-card/70 px-3 py-2">
          <div className="rounded-md bg-muted px-2 py-1 text-[9px] text-muted-foreground">
            guardiannest.net
          </div>
        </div>
        {/* Faux page */}
        <div className="flex-1 space-y-2 p-3">
          <div className="h-4 w-3/4 rounded bg-primary/30" />
          <div className="h-2 w-full rounded bg-muted-foreground/20" />
          <div className="h-2 w-5/6 rounded bg-muted-foreground/20" />
          <div className="mt-3 h-16 rounded-lg bg-primary/10" />
        </div>
        {/* Bottom tab bar with Share icon highlighted */}
        <div className="flex items-center justify-around border-t border-border/40 bg-card/80 py-2">
          <span className="h-3 w-3 rounded-full bg-muted-foreground/30" />
          <span className="h-3 w-3 rounded-full bg-muted-foreground/30" />
          <span className="flex h-6 w-6 items-center justify-center rounded-full ring-2 ring-primary">
            <ShareIcon className="h-3 w-3 text-primary" />
          </span>
          <span className="h-3 w-3 rounded-full bg-muted-foreground/30" />
        </div>
      </div>
    </PhoneFrame>
  );
}

function IosStep2() {
  return (
    <PhoneFrame hotspot={{ top: "58%", left: "62%" }}>
      {/* Faux share sheet */}
      <div className="flex h-full flex-col justify-end bg-background/80 backdrop-blur">
        <div className="rounded-t-2xl border border-border/40 bg-card p-2 shadow-lg">
          {/* Handle */}
          <div className="mx-auto mb-2 h-1 w-8 rounded-full bg-muted-foreground/40" />
          {/* Row: apps */}
          <div className="flex gap-2 pb-2">
            <div className="h-8 w-8 rounded-md bg-muted-foreground/20" />
            <div className="h-8 w-8 rounded-md bg-muted-foreground/20" />
            <div className="h-8 w-8 rounded-md bg-muted-foreground/20" />
          </div>
          <div className="mt-1 space-y-1 border-t border-border/40 pt-2">
            <div className="flex items-center justify-between rounded px-1 py-1 text-[9px] text-muted-foreground">
              <span>Copy Link</span>
              <div className="h-3 w-3 rounded-sm bg-muted-foreground/30" />
            </div>
            <div className="flex items-center justify-between rounded bg-primary/15 px-1 py-1 text-[9px] font-semibold text-primary ring-1 ring-primary/40">
              <span>Add to Home Screen</span>
              <Plus className="h-3 w-3" />
            </div>
            <div className="flex items-center justify-between rounded px-1 py-1 text-[9px] text-muted-foreground">
              <span>Add Bookmark</span>
              <div className="h-3 w-3 rounded-sm bg-muted-foreground/30" />
            </div>
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}

function IosStep3() {
  return (
    <PhoneFrame>
      {/* Faux home screen with GN icon */}
      <div className="grid h-full grid-cols-4 gap-2 bg-gradient-to-br from-primary/20 to-accent/10 p-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="aspect-square rounded-lg bg-muted-foreground/20" />
        ))}
        {/* GN icon highlighted */}
        <div className="col-start-2 flex aspect-square flex-col items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-md ring-2 ring-primary">
          <span className="font-heading text-sm font-extrabold">GN</span>
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="aspect-square rounded-lg bg-muted-foreground/20" />
        ))}
      </div>
    </PhoneFrame>
  );
}

function AndroidStep1() {
  return (
    <PhoneFrame hotspot={{ top: 10, right: 12 }}>
      <div className="flex h-full flex-col">
        {/* URL bar with ⋮ menu highlighted */}
        <div className="flex items-center gap-2 border-b border-border/40 bg-card/70 px-3 py-2">
          <div className="flex-1 rounded-md bg-muted px-2 py-1 text-[9px] text-muted-foreground">
            guardiannest.net
          </div>
          <span className="flex h-6 w-6 items-center justify-center rounded-full ring-2 ring-primary">
            <MoreVertical className="h-3 w-3 text-primary" />
          </span>
        </div>
        {/* Faux page */}
        <div className="flex-1 space-y-2 p-3">
          <div className="h-4 w-3/4 rounded bg-primary/30" />
          <div className="h-2 w-full rounded bg-muted-foreground/20" />
          <div className="h-2 w-5/6 rounded bg-muted-foreground/20" />
          <div className="mt-3 h-16 rounded-lg bg-primary/10" />
        </div>
      </div>
    </PhoneFrame>
  );
}

function AndroidStep2() {
  return (
    <PhoneFrame hotspot={{ top: "38%", right: 20 }}>
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-2 border-b border-border/40 bg-card/70 px-3 py-2">
          <div className="flex-1 rounded-md bg-muted px-2 py-1 text-[9px] text-muted-foreground">
            guardiannest.net
          </div>
          <MoreVertical className="h-3 w-3 text-muted-foreground/40" />
        </div>
        {/* Menu dropdown */}
        <div className="relative flex-1">
          <div className="absolute right-2 top-1 w-32 rounded-md border border-border/40 bg-card p-1 shadow-md">
            <div className="rounded px-2 py-1 text-[9px] text-muted-foreground">New tab</div>
            <div className="rounded px-2 py-1 text-[9px] text-muted-foreground">Bookmarks</div>
            <div className="rounded bg-primary/15 px-2 py-1 text-[9px] font-semibold text-primary ring-1 ring-primary/40">
              Install app
            </div>
            <div className="rounded px-2 py-1 text-[9px] text-muted-foreground">Share…</div>
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
export default function Install() {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [platform, setPlatform] = useState("desktop");
  const [installStatus, setInstallStatus] = useState(""); // "accepted" | "dismissed" | ""

  useEffect(() => {
    setPlatform(detectPlatform());

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches
    ) {
      setIsInstalled(true);
    }

    const onInstalled = () => setIsInstalled(true);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setInstallStatus(outcome);
      if (outcome === "accepted") {
        setIsInstalled(true);
        setDeferredPrompt(null);
      }
      // On "dismissed" we intentionally KEEP deferredPrompt so the
      // native-prompt block stays mounted and install-dismissed-note can
      // render, letting the user retry.
    } catch (e) {
      setInstallStatus("dismissed");
    } finally {
      setInstalling(false);
    }
  };

  const benefits = [
    {
      icon: Zap,
      title: "One-tap access",
      desc: "GuardianNest opens in a single tap from your home screen — no browser tabs to hunt for.",
    },
    {
      icon: Bell,
      title: "Timely reminders",
      desc: "Medication and appointment nudges arrive on the device you actually check.",
    },
    {
      icon: WifiOff,
      title: "Works offline",
      desc: "Recently viewed records and emergency info stay available even without a connection.",
    },
  ];

  const showIos = platform === "ios" || platform === "desktop";
  const showAndroid = platform === "android" || platform === "desktop";

  return (
    <div className="min-h-screen bg-background" data-testid="install-page">
      {/* Header */}
      <header className="border-b border-border">
        <div className="container mx-auto flex items-center justify-between px-6 py-5">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="install-back-home">
            <Logo size="sm" />
          </Link>
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-muted-foreground hover:text-foreground"
            data-testid="install-back"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="container mx-auto px-6 pt-12 pb-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mx-auto max-w-2xl text-center"
        >
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
            <Smartphone className="h-8 w-8 text-primary" />
          </div>
          <Badge className="mb-3 bg-primary/10 text-primary hover:bg-primary/10">
            Progressive Web App
          </Badge>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
            Put GuardianNest on your home screen
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Add the app to your phone in a few taps. No app store, no download, no waiting. It works
            just like a native app — one icon, one tap, straight to your family's care hub.
          </p>
        </motion.div>
      </section>

      {/* Install status / primary CTA */}
      <section className="container mx-auto px-6 pb-4">
        <div className="mx-auto max-w-2xl">
          {isInstalled ? (
            <div
              className="flex items-start gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-5"
              data-testid="install-already-done"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15">
                <Check className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-heading font-bold text-foreground">You're all set!</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  GuardianNest is already on your home screen. Open it from your device to pick up
                  right where you left off.
                </p>
              </div>
            </div>
          ) : deferredPrompt ? (
            <div
              className="rounded-2xl border border-primary/30 bg-primary/5 p-6 text-center"
              data-testid="install-native-prompt"
            >
              <Sparkles className="mx-auto mb-3 h-6 w-6 text-primary" />
              <p className="font-heading text-lg font-bold text-foreground">
                Your browser is ready to install
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                One tap and GuardianNest lands on your home screen.
              </p>
              <Button
                size="lg"
                className="mt-5"
                onClick={handleInstall}
                disabled={installing}
                data-testid="install-native-btn"
              >
                <Download className="mr-2 h-5 w-5" />
                {installing ? "Installing…" : "Install now"}
              </Button>
              {installStatus === "dismissed" && (
                <p
                  className="mt-3 text-xs text-muted-foreground"
                  data-testid="install-dismissed-note"
                >
                  No worries — you can install anytime from this page.
                </p>
              )}
            </div>
          ) : (
            <div
              className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground"
              data-testid="install-manual-note"
            >
              <p>
                Your browser can't install apps automatically — but adding GuardianNest to your home
                screen still only takes a few taps. Follow the guide below for your device.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Benefits row */}
      <section className="container mx-auto px-6 pt-10">
        <div className="grid gap-4 md:grid-cols-3">
          {benefits.map((b) => (
            <div
              key={b.title}
              className="rounded-2xl border border-border bg-card p-5"
              data-testid={`install-benefit-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <b.icon className="h-5 w-5" />
              </div>
              <h3 className="font-heading text-base font-bold text-foreground">{b.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* iOS walkthrough */}
      {showIos && (
        <section
          className="container mx-auto px-6 pt-16"
          data-testid="install-ios-guide"
        >
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary">
                iPhone / iPad
              </span>
              <h2 className="font-heading text-2xl font-extrabold tracking-tight">
                Install on Safari
              </h2>
            </div>

            <div className="mt-6 grid gap-6 md:grid-cols-3">
              <InstallStep
                num={1}
                title="Tap Share"
                desc={
                  <>
                    Open <strong>guardiannest.net</strong> in Safari and tap the{" "}
                    <ShareIcon className="inline h-4 w-4 align-text-bottom text-primary" /> Share
                    icon at the bottom.
                  </>
                }
                visual={<IosStep1 />}
              />
              <InstallStep
                num={2}
                title="Add to Home Screen"
                desc={
                  <>
                    Scroll the share sheet and tap{" "}
                    <strong className="text-foreground">Add to Home Screen</strong>.
                  </>
                }
                visual={<IosStep2 />}
              />
              <InstallStep
                num={3}
                title="Tap Add"
                desc="Confirm the name and tap Add. GuardianNest lands on your home screen."
                visual={<IosStep3 />}
              />
            </div>
          </div>
        </section>
      )}

      {/* Android walkthrough */}
      {showAndroid && (
        <section
          className="container mx-auto px-6 pt-16"
          data-testid="install-android-guide"
        >
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary">
                Android
              </span>
              <h2 className="font-heading text-2xl font-extrabold tracking-tight">
                Install on Chrome
              </h2>
            </div>

            <div className="mt-6 grid gap-6 md:grid-cols-3">
              <InstallStep
                num={1}
                title="Open the menu"
                desc={
                  <>
                    In Chrome, open <strong>guardiannest.net</strong> and tap the{" "}
                    <MoreVertical className="inline h-4 w-4 align-text-bottom text-primary" /> menu
                    in the top-right.
                  </>
                }
                visual={<AndroidStep1 />}
              />
              <InstallStep
                num={2}
                title="Install app"
                desc={
                  <>
                    Tap <strong className="text-foreground">Install app</strong> (or "Add to Home
                    Screen" on older versions).
                  </>
                }
                visual={<AndroidStep2 />}
              />
              <InstallStep
                num={3}
                title="You're in"
                desc="Chrome drops the GuardianNest icon on your home screen — tap it to launch."
                visual={<IosStep3 />}
              />
            </div>
          </div>
        </section>
      )}

      {/* Footer / return */}
      <section className="container mx-auto px-6 py-16">
        <div className="mx-auto flex max-w-2xl flex-col items-center gap-4 text-center">
          <p className="text-sm text-muted-foreground">
            Having trouble? Try opening this page in Safari (iPhone) or Chrome (Android) — some
            in-app browsers don't support home-screen installs.
          </p>
          <Link to="/">
            <Button variant="outline" data-testid="install-return-home">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to home
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

function InstallStep({ num, title, desc, visual }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ delay: (num - 1) * 0.08, duration: 0.35 }}
      className="flex flex-col items-center rounded-2xl border border-border bg-card p-5 text-center"
    >
      <div className="mb-4 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
        {num}
      </div>
      <h3 className="font-heading text-lg font-bold text-foreground">{title}</h3>
      <p className="mt-2 min-h-[3.5rem] text-sm text-muted-foreground">{desc}</p>
      <div className="mt-5 w-full">{visual}</div>
    </motion.div>
  );
}
